import React from 'react';
import { Link } from 'react-router-dom';

const SmartReminders = () => {
  const features = [
    {
      title: "Intelligent Scheduling",
      description: "AI-powered scheduling that adapts to patient preferences and behavior",
      icon: "📅",
      benefits: [
        "Personalized timing",
        "Preferred contact methods",
        "Smart rescheduling",
        "Waitlist management"
      ]
    },
    {
      title: "Multi-Channel Reminders",
      description: "Reach patients through their preferred communication channels",
      icon: "📱",
      benefits: [
        "SMS notifications",
        "Email reminders",
        "App notifications",
        "Voice calls"
      ]
    },
    {
      title: "Smart Follow-ups",
      description: "Automated follow-up system based on treatment type and patient history",
      icon: "🔄",
      benefits: [
        "Treatment-specific reminders",
        "Post-visit follow-ups",
        "Preventive care alerts",
        "Custom messaging"
      ]
    },
    {
      title: "Analytics & Insights",
      description: "Track and optimize reminder effectiveness",
      icon: "📊",
      benefits: [
        "Response tracking",
        "Engagement metrics",
        "Success rates",
        "ROI measurement"
      ]
    }
  ];

  const successMetrics = [
    {
      metric: "95%",
      label: "Reminder Delivery",
      description: "Successful delivery rate"
    },
    {
      metric: "40%",
      label: "No-Show Reduction",
      description: "Through smart reminders"
    },
    {
      metric: "85%",
      label: "Patient Satisfaction",
      description: "With reminder system"
    },
    {
      metric: "3x",
      label: "Response Rate",
      description: "Compared to traditional methods"
    }
  ];

  const testimonials = [
    {
      quote: "The smart reminder system has dramatically reduced our no-shows and improved patient satisfaction. The AI-powered timing is spot on!",
      author: "Dr. Emily Chen",
      practice: "Bright Smile Dental"
    },
    {
      quote: "Our patients love the flexibility of choosing their preferred reminder method. It's made a huge difference in our appointment management.",
      author: "Dr. Michael Rodriguez",
      practice: "Family Dental Care"
    }
  ];

  return (
    <div className="pt-24 bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              Smart Reminders
            </h1>
            <p className="text-xl text-gray-600">
              AI-powered appointment reminders and follow-ups that adapt to your patients' preferences
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-shadow"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-2xl">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Success Metrics */}
        <div className="bg-white rounded-2xl p-8 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-[#1E2A4A] mb-8 text-center">
            Proven Results
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {successMetrics.map((metric, index) => (
              <div 
                key={index}
                className="text-center"
              >
                <div className="text-4xl font-bold text-orange-500 mb-2">
                  {metric.metric}
                </div>
                <div className="text-lg font-semibold text-[#1E2A4A] mb-1">
                  {metric.label}
                </div>
                <div className="text-sm text-gray-600">
                  {metric.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <div className="text-2xl text-orange-500 mb-4">"</div>
              <p className="text-gray-600 mb-6">
                {testimonial.quote}
              </p>
              <div>
                <div className="font-bold text-[#1E2A4A]">
                  {testimonial.author}
                </div>
                <div className="text-gray-600">
                  {testimonial.practice}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-orange-500 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Your Reminder System?
          </h2>
          <p className="text-xl mb-8">
            Experience the power of AI-driven appointment management
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/schedule-demo"
              className="bg-white text-orange-500 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Schedule a Demo
            </Link>
            <Link 
              to="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-orange-500 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartReminders; 